import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXTwitter } from "@fortawesome/free-brands-svg-icons";

import smashImage from '../assets/illustration-webp/smash.webp';
import tekkenImage from '../assets/illustration-webp/tekken8.webp';
import streetfighterImage from '../assets/illustration-webp/sf6.webp';
import ogeyImage from '../assets/joueurs/ogey.jpg';
import neerozImage from '../assets/joueurs/neeroz.webp';
import crepeImage from '../assets/joueurs/crepe.jpg';
import bloomImage from '../assets/joueurs/bloom.webp';
import luugiImage from '../assets/joueurs/luggi.jpg';
import dombiliImage from '../assets/joueurs/dombili-2.jpg';
import wawaImage from '../assets/joueurs/wawa.jpg';

const games = [
 {
   id: 1,
   title: "Super Smash Bros",
   image: smashImage,
   roster: [
     { name: "Ogey", main: "Captain Falcon", image: ogeyImage, socials: "https://x.com/7Neeroz" },
     { name: "Neeroz", main: "Pikachu", image: neerozImage, socials: "https://x.com/7Neeroz" },
     { name: "crêpe salée", main: "Steve", image: crepeImage, socials: "https://x.com/crepesaleeSSB" },
     { name: "Bloom4Eva", main: "Bayonetta", image: bloomImage, socials: "https://x.com/Bloom4Evaa" },
     { name: "Luugi", main: "Luigi", image: luugiImage, socials: "https://x.com/Mario_LuigiBIS" }
   ]
 },
 {
   id: 2,
   title: "Tekken 8",
   image: tekkenImage,
   roster: [
     { name: "Dombili", main: "Feng", image: dombiliImage, socials: "https://x.com/DombiliMaymun" }
   ]
 },
 {
   id: 3,
   title: "Street Fighter 6",
   image: streetfighterImage,
   roster: [
     { name: "Wawa", main: "Cammy", image: wawaImage, socials: "https://x.com/Wawa_FGC" }
   ]
 }
];

const Roster = () => {
 const [currentIndex, setCurrentIndex] = useState(0);
 const [isTransitioning, setIsTransitioning] = useState(false);
 const [currentGame, setCurrentGame] = useState(games[0]);
 const [displayedGame, setDisplayedGame] = useState(games[0]);
 const [slideDirection, setSlideDirection] = useState('right');

 useEffect(() => {
   const style = document.createElement('style');
   style.textContent = `
     @keyframes slideInRight {
       from {
         transform: translateX(100%);
         opacity: 0;
       }
       to {
         transform: translateX(0);
         opacity: 1;
       }
     }

     @keyframes slideInLeft {
       from {
         transform: translateX(-100%);
         opacity: 0;
       }
       to {
         transform: translateX(0);
         opacity: 1;
       }
     }

     @keyframes slideOutRight {
       from {
         transform: translateX(0);
         opacity: 1;
       }
       to {
         transform: translateX(-100%);
         opacity: 0;
       }
     }

     @keyframes slideOutLeft {
       from {
         transform: translateX(0);
         opacity: 1;
       }
       to {
         transform: translateX(100%);
         opacity: 0;
       }
     }

     .slide-in-right {
       animation: slideInRight 500ms ease-in-out forwards;
     }

     .slide-in-left {
       animation: slideInLeft 500ms ease-in-out forwards;
     }

     .slide-out-right {
       animation: slideOutRight 500ms ease-in-out forwards;
     }

     .slide-out-left {
       animation: slideOutLeft 500ms ease-in-out forwards;
     }
   `;
   document.head.appendChild(style);
 }, []);

 const preloadImages = useCallback(() => {
   games.forEach(game => {
     const img = new Image();
     img.src = game.image;
   });
 }, []);

 useEffect(() => {
   preloadImages();
 }, [preloadImages]);

 const handleGameChange = useCallback((direction) => {
   if (isTransitioning) return;

   setIsTransitioning(true);
   setSlideDirection(direction);

   const newIndex = direction === 'right'
     ? (currentIndex + 1) % games.length
     : (currentIndex - 1 + games.length) % games.length;

   setCurrentGame(games[newIndex]);
   setCurrentIndex(newIndex);

   setTimeout(() => {
     setDisplayedGame(games[newIndex]);
     setIsTransitioning(false);
   }, 500);
 }, [currentIndex, isTransitioning]);

 return (
   <div className="w-full max-w-6xl mx-auto mt-10" style={{ backgroundColor: 'rgb(10, 1, 1)', color: '#e0e0e0' }}>
     <div className="relative overflow-hidden">
       <div className="relative h-56">
         <div className="relative w-full h-full">
           <div 
             className={`absolute inset-0 ${isTransitioning ? (slideDirection === 'right' ? 'slide-out-right' : 'slide-out-left') : ''}`}
           >
             <img
               src={displayedGame.image}
               alt={displayedGame.title}
               className="w-full h-full object-cover"
             />
             <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
             <div className="absolute bottom-0 left-0 right-0 p-4">
               <h2 className="text-2xl font-bold text-white tracking-tight">
                 {displayedGame.title}
               </h2>
             </div>
           </div>

           <div 
             className={`absolute inset-0 ${isTransitioning ? (slideDirection === 'right' ? 'slide-in-right' : 'slide-in-left') : ''}`}
             style={{ visibility: isTransitioning ? 'visible' : 'hidden' }}
           >
             <img
               src={currentGame.image}
               alt={currentGame.title}
               className="w-full h-full object-cover"
             />
             <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
             <div className="absolute bottom-0 left-0 right-0 p-4">
               <h2 className="text-2xl font-bold text-white tracking-tight">
                 {currentGame.title}
               </h2>
             </div>
           </div>
         </div>
       </div>

       <button
         onClick={() => handleGameChange('left')}
         className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full hover:bg-black/80 transition-colors z-10"
         disabled={isTransitioning}
       >
         <ChevronLeft />
       </button>
       <button
         onClick={() => handleGameChange('right')}
         className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full hover:bg-black/80 transition-colors z-10"
         disabled={isTransitioning}
       >
         <ChevronRight />
       </button>

       <div className="absolute bottom-4 right-4 flex space-x-2">
         {games.map((_, index) => (
           <button
             key={index}
             className={`w-8 h-1 rounded-full transition-colors ${
               index === currentIndex ? 'bg-pink-700' : 'bg-white/30'
             }`}
             onClick={() => !isTransitioning && setCurrentIndex(index)}
           />
         ))}
       </div>
     </div>

     <div className="px-6 py-8" style={{ backgroundColor: 'rgb(10, 1, 1)' }}>
       <h3 className="text-2xl font-bold mb-6 text-pink-700">Roster</h3>
       <div 
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
       >
         {displayedGame.roster.map((player, index) => (
           <div
             key={player.name}
             className="roster-card bg-neutral-900 rounded-lg overflow-hidden transform transition-all duration-300"
           >
             <div className="p-4 border-l-4 border-[#be185d]">
               <div className="flex items-center mb-4">
                 <div className="img-container w-24 h-24 rounded-full mr-4 overflow-hidden">
                   <img
                     src={player.image}
                     alt={player.name}
                     className="w-full h-full object-cover"
                   />
                 </div>
                 <h4 className="font-bold text-xl text-white">{player.name}</h4>
               </div>
               <div className="space-y-1">
                 <div className="flex items-center">
                   <span className="main-title text-[#be185d] font-semibold text-lg">Main :</span>
                   <span className="main-txt text-gray-300 text-base pl-2">{player.main}</span>
                 </div>
               </div>
               {player.socials && (
                 <div className="player-socials mt-4">
                   <a 
                     href={player.socials} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="text-gray-400 hover:text-[#d32f2f] transition-colors"
                   >
                     <FontAwesomeIcon icon={faXTwitter} />
                   </a>
                 </div>
               )}
             </div>
           </div>
         ))}
       </div>
     </div>
   </div>
 );
};

export default Roster;